import { useState, useEffect, useCallback, useRef } from 'react';
import operationFailureApi, {
  type OperationFailure,
  type OperationFailureCounts,
} from '../services/operationFailureApi';

/**
 * useOperationFailures
 *
 * Feeds the failed-operations centre: the header badge (always live) and the
 * modal list (only while it's open).
 *
 * The counts poll runs regardless of `enabled` because the header button has
 * to show a badge before anyone opens anything — that badge IS the discovery
 * mechanism for the whole feature. The list only loads when the modal is open.
 */

interface UseOperationFailuresOptions {
  /** True while the modal is open — gates the (heavier) list fetch. */
  enabled?: boolean;
  /** How often to refresh the badge counts. */
  pollMs?: number;
}

const DEFAULT_POLL_MS = 60_000;

export function useOperationFailures({
  enabled = false,
  pollMs = DEFAULT_POLL_MS,
}: UseOperationFailuresOptions = {}) {
  const [failures, setFailures] = useState<OperationFailure[]>([]);
  const [counts, setCounts] = useState<OperationFailureCounts>({ errors: 0, warnings: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Guards against a slow earlier request landing after a newer one and
  // resurrecting rows the dispatcher just resolved.
  const requestIdRef = useRef(0);

  const fetchCounts = useCallback(async () => {
    try {
      setCounts(await operationFailureApi.counts());
    } catch {
      // A failed poll must not blank the badge — keep the last known value.
    }
  }, []);

  const fetchFailures = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const { items } = await operationFailureApi.list({ status: 'open', limit: 200 });
      if (requestId !== requestIdRef.current) return;
      setFailures(items);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      setError((err as Error).message || 'Failed to load operation failures');
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, []);

  const resolve = useCallback(async (id: number) => {
    // Optimistic: the row disappears immediately and the badge ticks down.
    // A failed call re-fetches, so a wrong guess self-corrects.
    const removed = failures.find(f => f.id === id);
    setFailures(prev => prev.filter(f => f.id !== id));
    if (removed) {
      setCounts(prev => ({
        errors: removed.severity === 'error' ? Math.max(0, prev.errors - 1) : prev.errors,
        warnings: removed.severity === 'warn' ? Math.max(0, prev.warnings - 1) : prev.warnings,
      }));
    }
    try {
      await operationFailureApi.resolve(id);
    } catch (err) {
      setError((err as Error).message || 'Failed to resolve');
      await fetchFailures();
      await fetchCounts();
    }
  }, [failures, fetchFailures, fetchCounts]);

  const resolveAll = useCallback(async () => {
    setFailures([]);
    setCounts({ errors: 0, warnings: 0 });
    try {
      await operationFailureApi.resolveAll();
    } catch (err) {
      setError((err as Error).message || 'Failed to clear');
      await fetchFailures();
      await fetchCounts();
    }
  }, [fetchFailures, fetchCounts]);

  // Badge counts: always on, independent of the modal.
  useEffect(() => {
    fetchCounts();
    const timer = setInterval(fetchCounts, pollMs);
    return () => clearInterval(timer);
  }, [fetchCounts, pollMs]);

  // List: only while the modal is open.
  useEffect(() => {
    if (!enabled) return;
    fetchFailures();
  }, [enabled, fetchFailures]);

  return {
    failures,
    counts,
    loading,
    error,
    refresh: fetchFailures,
    refreshCounts: fetchCounts,
    resolve,
    resolveAll,
  };
}
